/**
 * use-delayed-paywall
 *
 * Returns a memoized trigger that schedules opening the monetization paywall after a short delay.
 *
 * Primary responsibilities:
 * - Keep a timeout reference so delayed paywall openings are deduplicated.
 * - Clear the timer when the component unmounts.
 * - Reuse `scheduleDelayedPaywall` to respect the shared delay value and client-only guard.
 *
 * Dependencies:
 * - `scheduleDelayedPaywall` from `lib/subscription`
 */
import { useCallback, useEffect, useRef } from 'react'
import { scheduleDelayedPaywall } from '@/lib/subscription'

export function useDelayedPaywall(openPaywall?: () => void) {
  const timerRef = useRef<ReturnType<typeof window.setTimeout> | null>(null)

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current)
      }
    }
  }, [])

  return useCallback(() => {
    if (!openPaywall) {
      return
    }

    if (timerRef.current) {
      clearTimeout(timerRef.current)
    }

    timerRef.current = scheduleDelayedPaywall(openPaywall)
  }, [openPaywall])
}
